import React, { useEffect, useMemo, useState } from "react"
import ModalPortal from "../Modal/ModalPortal"
import { GenericDropDown } from "../Dropdown/Dropdown"
import GetSpace from "../../utils/space"
import Button, { ButtonIcon } from "../Button/Button"
import { useTranslation } from "react-i18next"
import type { PrintTargetOption } from "../../hooks/Order/useSavePrint"
import type { PrintCommandType } from "../../hooks/Order/useSavePrint"
import { useSavePrint } from "../../hooks/Order/useSavePrint"
import { faClose, faPrint } from "@fortawesome/free-solid-svg-icons"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import Animate, { AnimationTypes } from "../Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

interface OrderPrintOptionsPupopProps {
  isOpen: boolean
  title: string
  orderId: number
  printScope: "customer" | "fulfillment" | "loja" | "delivery"
  onClose: () => void
  onPrinted?: () => void
}

export const OrderPrintOptionsPupop: React.FC<OrderPrintOptionsPupopProps> = ({
  isOpen,
  title,
  orderId,
  printScope,
  onClose,
  onPrinted,
}) => {
  const { t } = useTranslation("general")

  const { getActivePrintTargets, handlePrintOrderToCustomer, handlePrintOrderToFulfillment } = useSavePrint()

  const [isLoadingOptions, setIsLoadingOptions] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [options, setOptions] = useState<PrintTargetOption[]>([])

  const [selectedValue, setSelectedValue] = useState("download")

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setIsLoadingOptions(true)

    const commandType: PrintCommandType =
      printScope === "fulfillment" ? "fulfillment" : printScope === "loja" ? "loja" : printScope === "delivery" ? "delivery" : "all"

    void getActivePrintTargets(commandType)
      .then((loadedOptions) => {
        setOptions(loadedOptions)
        setSelectedValue(loadedOptions[0]?.value ?? "download")
      })
      .finally(() => setIsLoadingOptions(false))
  }, [isOpen])

  const resolvedOptions = useMemo(
    () => (options.length > 0 ? options : [{ label: t("buttons:button_download"), value: "download" }]),
    [options, t],
  )

  const selectedOption = resolvedOptions.find((option) => option.value === selectedValue)

  const handleConfirmPrint = async () => {
    const printerConfigurationId = selectedOption?.printerConfigurationId
    setIsSubmitting(true)

    try {
      if (printScope === "fulfillment" || printScope === "loja" || printScope === "delivery") {
        await handlePrintOrderToFulfillment(orderId, onClose, printerConfigurationId)
      } else {
        await handlePrintOrderToCustomer(orderId, onClose, printerConfigurationId)
      }

      onPrinted?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={title}
              icon={faPrint}
              subtitleChildren={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"

                  descriptionKey="interactionDescriptions:components.orderPrintOptionsPupop.orderPrintOptionsPupop.button.onClose" />
              }
            />

            <div className="p-2">
              <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
                <p className="mt-2 text-sm text-slate-500">{t("label_print_target_description")}</p>
              </Animate>

              <GetSpace top={12} />

              <GenericDropDown
                name="print-target"
                data={resolvedOptions.map((option) => ({ label: option.label, value: option.value }))}
                initialState={selectedValue}
                handlerFuncion={(event) => setSelectedValue(event.currentTarget.value)}
                disable={isLoadingOptions || isSubmitting}

                  descriptionKey="interactionDescriptions:components.orderPrintOptionsPupop.orderPrintOptionsPupop.select.printTarget" />

              <GetSpace top={16} />

              <div className="flex justify-end gap-2">
                <Button
                  label={t("label_print_target_confirm")}
                  bgColor="green"
                  onClick={() => void handleConfirmPrint()}
                  disabled={isLoadingOptions || isSubmitting}

                  descriptionKey="interactionDescriptions:components.orderPrintOptionsPupop.orderPrintOptionsPupop.button.printTargetConfirm" />
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
